import {
  computed,
  inject,
  Injectable,
  signal,
  WritableSignal
} from '@angular/core';
import { finalize, tap } from 'rxjs';
import { ProjectStateService } from '../state/project-state.service';
import { AcsMode } from '../state/project.interface';
import {
  AuthEntity,
  AuthStateInterface,
  AuthStateService
} from '../state/auth-state.service';
import { Router } from '@angular/router';
import { Routing } from '../../../app.routes';
import { FormGroup } from '@angular/forms';
import { SaveProjectRequest } from '../http/dto';
import { DatePipe } from '@angular/common';
import { StepperService } from '../stepper.service';
import { IAcsHttpService } from '../http/acs/acs-http-service.interface';
import { ACS_HTTP_SERVICE } from '../http/http-service.token';
import { Beneficiary } from '../../../core/api/models/beneficiary';
import { PricingInformations } from '../../../core/api/models/pricing-informations';
import { ExpatClient } from '../../../core/api/models/expat-client';
import { jwtDecode } from 'jwt-decode';

@Injectable({
  providedIn: 'root'
})
export class SubscriptionFeatureService {
  readonly acsHttpService: IAcsHttpService = inject(ACS_HTTP_SERVICE);
  readonly authStateService: AuthStateService = inject(AuthStateService);
  readonly router: Router = inject(Router);
  readonly projectStateService: ProjectStateService =
    inject(ProjectStateService);
  readonly stepperService: StepperService = inject(StepperService);
  readonly datePipe: DatePipe = new DatePipe('fr-FR');
  private _isLoading: WritableSignal<boolean> = signal(false);

  constructor() {}

  get isLoading() {
    return computed(() => this._isLoading());
  }

  public getClientInfo(uuidClient: string, producerCode: string, mode: string) {
    return this.acsHttpService.getExpatClientInfos(uuidClient).pipe(
      tap((value) => {
        this.projectStateService.updateProjectInfo({
          client: value,
          producer_code: producerCode,
          mode: mode as AcsMode,
          subscription: false
        });
        this.projectStateService.updateClient(value);
      })
    );
  }

  public getProjectInfo(
    uuidProject: string,
    producerCode: string | null,
    mode: string | null
  ) {
    return this.acsHttpService.getProjectByUuid(uuidProject).pipe(
      tap((value) => {
        this.projectStateService.updateProjectInfo({
          beneficiaries: value.beneficiaries,
          product: value.product,
          pricing: value.pricing,
          producer_code: producerCode,
          mode: mode as AcsMode
        });
        this.projectStateService.updateClient(value.client);
      })
    );
  }

  public newProjectForSameClient() {
    this._isLoading.set(true);
    const authState = this.authStateService.selectAuthState()();
    const paramInfo = this.authStateService.getSessionStorageUrlParamInfo();
    const token = authState.token ?? paramInfo?.token ?? null;
    const tokenProducer =
      authState.token_producer ?? paramInfo?.token_producer ?? null;
    const uuid = this.getCurrentProjectUuid(authState, paramInfo);

    if (!token || !uuid) {
      this._isLoading.set(false);
      return;
    }

    // Le token du projet prouve l'accès au projet source, le token apporteur désigne l'apporteur
    // du devis dupliqué : sans lui, le back retombe sur l'apporteur ACS.
    this.acsHttpService
      .getInfoForNewProjectForSameClient(
        {
          token,
          token_producer: tokenProducer,
          mode: this.authStateService.selectAcsMode()()
        },
        uuid
      )
      .pipe(finalize(() => this._isLoading.set(false)))
      .subscribe({
        error: (_) => {
          console.error('Error when creating new project');
        },
        next: (value) => {
          const tokenData = (
            (
              value as {
                data?: { token?: string; token_producer?: string | null };
              }
            ).data ??
            (value as { token?: string; token_producer?: string | null })
          );

          if (!tokenData.token) {
            console.error('Missing token when creating new project');
            return;
          }

          const producerCode =
            authState.producerCode ?? paramInfo?.producerCode ?? null;
          const mode = authState.mode ?? paramInfo?.mode ?? null;
          const tokenPayload = this.extractPayloadFromToken(tokenData.token);
          const entity = tokenPayload?.entity ?? 'policies';
          const referrer = authState.referrer ?? paramInfo?.referrer ?? null;
          const newUuid = tokenPayload?.uuid ?? uuid;
          const nextTokenProducer = tokenData.token_producer ?? tokenProducer;

          this.authStateService.setToken(tokenData.token);
          this.authStateService.updateData({
            token: tokenData.token,
            uuid: newUuid,
            producerCode,
            mode,
            entity,
            referrer,
            token_producer: nextTokenProducer
          });
          this.projectStateService.setIsNewClient(false);
          this.stepperService.step.set(0);
          this.stepperService.isVisible.set(true);
          this.router
            .navigate([Routing.PROJECT], {
              queryParams: {
                token: tokenData.token,
                uuid: newUuid,
                producer_code: producerCode ?? undefined,
                mode: mode ?? undefined,
                entity,
                token_producer: nextTokenProducer ?? undefined
              }
            })
            .then();
        }
      });
  }

  public newProjectForNewClient() {
    this.projectStateService.reset();
    this.authStateService.reset();
    this.stepperService.step.set(0);
    this.stepperService.isVisible.set(true);
    this.router.navigate([Routing.PROJECT]).then();
  }

  public validateStep1(form: FormGroup) {
    const formValue = form.value;
    if (new Date(formValue.effect_date).getTimezoneOffset() > 0) {
      formValue.effect_date = `${formValue.effect_date.getFullYear()}-${(formValue.effect_date.getMonth() + 1).toString().padStart(2, '0')}-${formValue.effect_date.getUTCDate().toString().padStart(2, '0')}T00:00:00Z`;
    } else {
      formValue.effect_date =
        this.datePipe.transform(formValue.effect_date, 'yyyy-MM-dd') +
        'T00:00:00Z';
    }
    this.projectStateService.updatePricingInformations(formValue);
  }

  public validateStep2() {
    return this.acsHttpService.postStep2(this.toSaveProjectRequest());
  }

  public validateSubmitStep3(form: FormGroup) {
    const dataToSave = this.updateDataForStep3(form);
    return this.acsHttpService.postStep3Subscribe(dataToSave);
  }

  public validateSaveStep3(form: FormGroup) {
    const dataToSave = this.updateDataForStep3(form);
    return this.acsHttpService.postStep3Save(dataToSave);
  }

  private updateDataForStep3(form: FormGroup) {
    const dataToSave = this.toSaveProjectRequest();
    dataToSave.beneficiaries.forEach(
      (item) =>
        (item.acs_expat_beneficiary_maternity_option =
          item.category === form.get('beneficiaries')!.value ? 1 : 0)
    );
    this.projectStateService.updateExpatClientInfos(
      {
        ...dataToSave.client,
        beneficiaries: [...dataToSave.beneficiaries]
      },
      true
    );
    this.projectStateService.setProjectBeneficiaries(dataToSave.beneficiaries);
    return dataToSave;
  }

  public validateStep4() {
    const data = this.toSaveProjectRequest();
    data.subscription = true;
    return this.acsHttpService.postStep4(data);
  }

  private toSaveProjectRequest(): SaveProjectRequest {
    const pricing =
      this.projectStateService.getPricingInformations() ?? undefined;
    const client =
      this.projectStateService.selectExpatClientInfos() ?? undefined;
    const authData = this.authStateService.selectAuthState();
    const beneficiaries = this.ensureSouscripteur(
      [...this.projectStateService.selectProjectBeneficiaries()],
      client,
      pricing
    );
    this.projectStateService.setProjectBeneficiaries(beneficiaries);
    return {
      pricing: pricing,
      client: client,
      producer_code: authData().producerCode,
      beneficiaries: beneficiaries,
      token: authData().token ?? null,
      // selectAcsMode() et non authData().mode : c'est lui qui résout le mode `client` du parcours
      // grand public, indispensable au back pour poser `business_origin = Internet`.
      mode: this.authStateService.selectAcsMode()(),
      product: pricing ? pricing.currency ?? null : null,
      subscription: false,
      token_producer: authData().token_producer ?? null
    };
  }

  private ensureSouscripteur(
    beneficiaries: Beneficiary[],
    client?: ExpatClient,
    pricing?: PricingInformations
  ): Beneficiary[] {
    if (!client) {
      return beneficiaries;
    }

    const existingIndex = beneficiaries.findIndex(
      (b) => b.category === 'Souscripteur'
    );
    const subscriber: Beneficiary = {
      category: 'Souscripteur',
      birth_date: client.birth_date,
      acs_expat_beneficiary_business_sector:
        beneficiaries[existingIndex]?.acs_expat_beneficiary_business_sector ??
        0,
      acs_expat_beneficiary_email: client.email,
      // expat_project_maternity_option est le niveau d'option retenu sur le projet, pas le bénéficiaire
      // concerné : le reprendre ici marquait le souscripteur en plus du bénéficiaire réellement choisi.
      acs_expat_beneficiary_maternity_option:
        beneficiaries[existingIndex]?.acs_expat_beneficiary_maternity_option ??
        0,
      acs_expat_beneficiary_nationality_country:
        pricing?.expat_project_nationality ??
        beneficiaries[existingIndex]
          ?.acs_expat_beneficiary_nationality_country ??
        client.country ??
        '',
      acs_expat_beneficiary_occupation:
        beneficiaries[existingIndex]?.acs_expat_beneficiary_occupation ?? '',
      acs_expat_beneficiary_ppe:
        beneficiaries[existingIndex]?.acs_expat_beneficiary_ppe ?? false,
      first_name: client.first_name,
      last_name: client.last_name
    };

    if (existingIndex !== -1) {
      beneficiaries[existingIndex] = subscriber;
      return beneficiaries;
    }

    return [...beneficiaries, subscriber];
  }

  public setIsLoading(value: boolean) {
    this._isLoading.set(value);
  }

  private getCurrentProjectUuid(
    authState: AuthStateInterface,
    paramInfo: AuthStateInterface | null
  ): string | null {
    const tokenPayload = this.extractPayloadFromToken(
      authState.token ?? paramInfo?.token ?? null
    );

    if (tokenPayload?.entity === 'policies' && tokenPayload.uuid) {
      return tokenPayload.uuid;
    }

    if (authState.entity === 'policies') {
      return authState.uuid ?? paramInfo?.uuid ?? tokenPayload?.uuid ?? null;
    }

    if (paramInfo?.entity === 'policies') {
      return paramInfo.uuid ?? tokenPayload?.uuid ?? null;
    }

    return tokenPayload?.uuid ?? authState.uuid ?? paramInfo?.uuid ?? null;
  }

  private extractPayloadFromToken(
    token: string | null | undefined
  ): { uuid?: string; entity?: AuthEntity } | null {
    if (!token) {
      return null;
    }

    try {
      return jwtDecode<{ uuid?: string; entity?: AuthEntity }>(token);
    } catch {
      return null;
    }
  }
}
